import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import api from '../../services/api';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, getCurrencySymbol } from '../../utils/currency';

interface ProjectFormProps {
    onClose: () => void;
    onSuccess: () => void;
    project?: any;
    customers?: any[];
    developers?: any[];
    defaultCustomerId?: string;
}

interface TaskData {
    id?: string;
    taskName: string;
    isBillable: boolean;
}

interface InstallmentData {
    id?: string;
    amount: number | string;
    dueDate: string;
    status: string;
}

interface DeveloperData {
    id: string;
    name: string;
}

interface ProjectDeveloperData {
    id?: string;
    developerId: string;
    amount: number | string;
    installments: InstallmentData[];
}

interface ProjectFormData {
    projectName: string;
    projectCode: string;
    customerId: string;
    billingMethod: string;
    hourlyRate: number | string;
    receivedAmount: number;
    startDate: string;
    endDate: string;
    description: string;
    tasks: TaskData[];
    installments: InstallmentData[];
    developers: ProjectDeveloperData[];
}

const ProjectForm: React.FC<ProjectFormProps> = ({ onClose, onSuccess, project, customers: propCustomers, developers: propDevelopers, defaultCustomerId }) => {
    const { currency } = useAuth();
    const [fetchedCustomers, setFetchedCustomers] = useState<any[]>([]);
    const [fetchedDevelopers, setFetchedDevelopers] = useState<any[]>([]);

    useEffect(() => {
        const fetchMissingDependencies = async () => {
            if (!propCustomers) {
                const res = await api.get('/customers?limit=100');
                setFetchedCustomers(res.data.customers || []);
            }
            if (!propDevelopers) {
                const res = await api.get('/developers?limit=100');
                setFetchedDevelopers(res.data.developers || []);
            }
        };
        fetchMissingDependencies();
    }, [propCustomers, propDevelopers]);

    const customers = propCustomers || fetchedCustomers;
    const developers = propDevelopers || fetchedDevelopers;

    const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<ProjectFormData>({
        defaultValues: {
            projectName: '',
            projectCode: '',
            customerId: defaultCustomerId || '',
            billingMethod: 'Hourly Rate Per Project',
            hourlyRate: 17.00,
            receivedAmount: 0,
            startDate: '',
            endDate: '',
            description: '',
            tasks: [],
            installments: [],
            developers: []
        }
    });

    const billingMethod = watch('billingMethod');
    const hourlyRate = watch('hourlyRate');
    const receivedAmount = watch('receivedAmount');

    useEffect(() => {
        if (defaultCustomerId && !project) {
            setValue('customerId', defaultCustomerId);
        }
    }, [defaultCustomerId, fetchedCustomers, propCustomers, project, setValue]);

    const { fields: taskFields, append: appendTask, remove: removeTask } = useFieldArray({
        control,
        name: "tasks",
        keyName: "key"
    });

    const { fields: installmentFields, append: appendInstallment, remove: removeInstallment } = useFieldArray({
        control,
        name: "installments",
        keyName: "key"
    });

    const { fields: developerFields, append: appendDeveloper, remove: removeDeveloper } = useFieldArray({
        control,
        name: "developers",
        keyName: "key"
    });

    useEffect(() => {
        if (project) {
            // Reformat dates for inputs
            const formattedProject = {
                ...project,
                customerId: project.customerId || (project.customer ? project.customer.id : ''),
                startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
                endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
                installments: project.installments?.map((i: any) => ({
                    ...i,
                    dueDate: i.dueDate ? new Date(i.dueDate).toISOString().split('T')[0] : ''
                })) || [],
                developers: project.developers?.map((d: any) => ({
                    ...d,
                    developerId: d.developerId || (d.developer ? d.developer.id : ''),
                    installments: d.installments?.map((i: any) => ({
                        ...i,
                        dueDate: i.dueDate ? new Date(i.dueDate).toISOString().split('T')[0] : ''
                    })) || []
                })) || []
            };
            reset(formattedProject);
        }
    }, [project, customers.length, developers.length, reset]);

    const onSubmit = async (data: any) => {
        try {
            const formattedData = {
                ...data,
                hourlyRate: parseFloat(data.hourlyRate) || 0,
                receivedAmount: parseFloat(data.receivedAmount) || 0,
            };

            if (project) {
                await api.put(`/projects/${project.id}`, formattedData);
            } else {
                await api.post('/projects', formattedData);
            }
            onSuccess();
        } catch (err: any) {
            console.error('Failed to save project', err);
            const message = err.response?.data?.message || err.message || 'Failed to save project';
            alert(`Error: ${message}`);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {project ? 'Edit Project' : 'New Project'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <form id="project-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        {/* Project Name */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Project Name*</label>
                            <input
                                type="text"
                                {...register('projectName', { required: 'Project Name is required' })}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        {errors.projectName && <p className="text-red-500 text-sm ml-32">{errors.projectName.message as string}</p>}

                        {/* Project Code */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Project Code</label>
                            <input
                                type="text"
                                {...register('projectCode')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Start & End Date */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Start Date</label>
                            <input
                                type="date"
                                {...register('startDate')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">End Date</label>
                            <input
                                type="date"
                                {...register('endDate')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Customer */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Customer*</label>
                            <select
                                {...register('customerId', { required: 'Customer is required' })}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">Select Customer</option>
                                {customers.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.displayName}</option>
                                ))}
                            </select>
                        </div>
                        {errors.customerId && <p className="text-red-500 text-sm ml-32">{errors.customerId.message as string}</p>}

                        {/* Billing Method */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Billing Method*</label>
                            <select
                                {...register('billingMethod', { required: 'Billing Method is required' })}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="Hourly Rate Per Project">Hourly Rate Per Project</option>
                                <option value="Fixed Cost">Fixed Cost</option>
                                <option value="Hourly Rate Per Task">Hourly Rate Per Task</option>
                            </select>
                        </div>

                        {/* Hourly Rate / Fixed Price */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">
                                {billingMethod === 'Fixed Cost' ? 'Fixed Price' : 'Hourly Rate'}
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                placeholder={billingMethod === 'Fixed Cost' ? 'Fixed Price' : 'Hourly Rate'}
                                {...register('hourlyRate')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Received Amount & Remaining Amount (Fixed Cost only) */}
                        {billingMethod === 'Fixed Cost' && (
                            <>
                                <div className="flex gap-6">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2">Received Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Received Amount"
                                        {...register('receivedAmount')}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                    />
                                </div>
                                <div className="flex gap-6">
                                    <label className="text-sm font-medium text-gray-700 w-32 pt-2">Remaining Amount</label>
                                    <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-medium">
                                        {formatCurrency((Number(hourlyRate) || 0) - (Number(receivedAmount) || 0), currency)}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Description */}
                        <div className="flex gap-6">
                            <label className="text-sm font-medium text-gray-700 w-32 pt-2">Description</label>
                            <textarea
                                {...register('description')}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md h-24"
                            />
                        </div>

                        {/* Tasks Section */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-800">Tasks</h3>
                                <button
                                    type="button"
                                    onClick={() => appendTask({ taskName: '', isBillable: true })}
                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                                >
                                    <Plus size={16} /> Add Task
                                </button>
                            </div>

                            <div className="bg-gray-50 rounded-md p-4 space-y-3">
                                {taskFields.map((field, index) => (
                                    <div key={field.key} className="flex gap-4 items-center">
                                        <input
                                            type="hidden"
                                            {...register(`tasks.${index}.id` as const)}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Task Name"
                                            {...register(`tasks.${index}.taskName` as const, { required: true })}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeTask(index)}
                                            className="text-red-500 hover:text-red-700 p-2"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Installments Section */}
                        {billingMethod === 'Fixed Cost' && (
                            <div className="border-t border-gray-200 pt-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-800">Payment Installments</h3>
                                    <button
                                        type="button"
                                        onClick={() => appendInstallment({ amount: '', dueDate: '', status: 'Pending' })}
                                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                                    >
                                        <Plus size={16} /> Add Installment
                                    </button>
                                </div>

                                {installmentFields.length > 0 ? (
                                    <div className="bg-gray-50 rounded-md p-4 space-y-3">
                                        <div className="flex gap-4 mb-2 px-2">
                                            <div className="flex-1 text-xs font-semibold text-gray-500 uppercase">Amount</div>
                                            <div className="flex-1 text-xs font-semibold text-gray-500 uppercase">Due Date</div>
                                            <div className="w-32 text-xs font-semibold text-gray-500 uppercase">Status</div>
                                            <div className="w-10"></div>
                                        </div>
                                        {installmentFields.map((field, index) => (
                                            <div key={field.key} className="flex gap-4 items-center">
                                                <input
                                                    type="hidden"
                                                    {...register(`installments.${index}.id` as const)}
                                                />
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Amount"
                                                    {...register(`installments.${index}.amount` as const, { required: true })}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                                <input
                                                    type="date"
                                                    {...register(`installments.${index}.dueDate` as const, { required: true })}
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                                <select
                                                    {...register(`installments.${index}.status` as const)}
                                                    className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="Paid">Paid</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() => removeInstallment(index)}
                                                    className="text-red-500 hover:text-red-700 p-2"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        ))}

                                        {/* Total Check */}
                                        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center px-2">
                                            <span className="text-sm font-medium text-gray-700">Total Installments Amount:</span>
                                            <span className={`font-semibold ${installmentFields.reduce((sum, _, i) => sum + Number(watch(`installments.${i}.amount`) || 0), 0) !== Number(hourlyRate)
                                                ? 'text-orange-500'
                                                : 'text-green-600'
                                                }`}>
                                                {formatCurrency(installmentFields.reduce((sum, _, i) => sum + Number(watch(`installments.${i}.amount`) || 0), 0), currency)}
                                                {' '} / {formatCurrency(Number(hourlyRate || 0), currency)}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500 italic">No installments added yet.</p>
                                )}
                            </div>
                        )}

                        {/* Developers Assignment Section */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-lg font-medium text-gray-800">Assigned Developers</h3>
                                    <p className="text-sm text-gray-500">Assign developers to this project and manage their payouts.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => appendDeveloper({ developerId: '', amount: '', installments: [] })}
                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium"
                                >
                                    <Plus size={16} /> Assign Developer
                                </button>
                            </div>

                            {developerFields.length > 0 ? (
                                <div className="space-y-6">
                                    {developerFields.map((devField, devIndex) => (
                                        <div key={devField.key} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex gap-4 w-2/3">
                                                    <input
                                                        type="hidden"
                                                        {...register(`developers.${devIndex}.id` as const)}
                                                    />
                                                    <select
                                                        {...register(`developers.${devIndex}.developerId` as const, { required: true })}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                                    >
                                                        <option value="">Select Developer...</option>
                                                        {developers.map((d: any) => (
                                                            <option key={d.id} value={d.id}>{d.name}</option>
                                                        ))}
                                                    </select>
                                                    <div className="relative flex-1">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder="Total Contract Amount"
                                                            {...register(`developers.${devIndex}.amount` as const, { required: true })}
                                                            className="w-full px-3 py-2 pl-6 border border-gray-300 rounded-md"
                                                        />
                                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">{getCurrencySymbol(currency)}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeDeveloper(devIndex)}
                                                    className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-md transition"
                                                    title="Remove assigned developer"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>

                                            {/* Developer Installments Section Component Call */}
                                            <div className="pl-6 pt-4 border-t border-gray-200 mt-4">
                                                <DeveloperInstallmentsArray
                                                    control={control}
                                                    register={register}
                                                    devIndex={devIndex}
                                                    watch={watch}
                                                    currency={currency}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500">
                                    No developers assigned. Click "Assign Developer" to track subcontractor payouts.
                                </div>
                            )}
                        </div>

                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-3">
                    <button
                        type="submit"
                        form="project-form"
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Save size={18} />
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper component to manage the nested field array for a specific Developer's installments
const DeveloperInstallmentsArray = ({ control, register, devIndex, watch, currency }: any) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: `developers.${devIndex}.installments`,
        keyName: "key"
    });

    const devTotalAmount = Number(watch(`developers.${devIndex}.amount`) || 0);
    const sumInstallments = fields.reduce((sum, _, i) => sum + Number(watch(`developers.${devIndex}.installments.${i}.amount`) || 0), 0);

    return (
        <div>
            <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-600">Their Installments</span>
                <button
                    type="button"
                    onClick={() => append({ amount: '', dueDate: '', status: 'Pending' })}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 text-xs font-medium"
                >
                    <Plus size={14} /> Add Installment
                </button>
            </div>

            {fields.length > 0 ? (
                <div className="space-y-2">
                    {fields.map((field, i) => (
                        <div key={field.key} className="flex gap-3 items-center text-sm">
                            <input
                                type="hidden"
                                {...register(`developers.${devIndex}.installments.${i}.id`)}
                            />
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                {...register(`developers.${devIndex}.installments.${i}.amount`, { required: true })}
                                className="w-1/3 px-2 py-1 border border-gray-300 rounded"
                            />
                            <input
                                type="date"
                                {...register(`developers.${devIndex}.installments.${i}.dueDate`, { required: true })}
                                className="w-1/3 px-2 py-1 border border-gray-300 rounded"
                            />
                            <select
                                {...register(`developers.${devIndex}.installments.${i}.status`)}
                                className="w-1/4 px-2 py-1 border border-gray-300 rounded"
                            >
                                <option value="Pending">Pending</option>
                                <option value="Paid">Paid</option>
                            </select>
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="text-red-400 hover:text-red-600"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                    <div className="text-xs text-right mt-2 pr-8">
                        <span className="text-gray-500 mr-2">Sum:</span>
                        <span className={sumInstallments !== devTotalAmount ? 'text-orange-500 font-semibold' : 'text-green-600 font-semibold'}>
                            {formatCurrency(sumInstallments, currency)} / {formatCurrency(devTotalAmount, currency)}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="text-xs text-gray-400 italic">No installments specified for this developer yet.</div>
            )}
        </div>
    );
};

export default ProjectForm;
